import { useState, useCallback, useEffect } from 'react';
import { useNotification } from '../../../contexts/admin/NotificationContext';
import { User, UserFilterParams, UserBulkActionRequest, UserUpdatePayload } from '../../../types/users.types';
import { useGenericManagement } from '../useGenericManagement';
import { ITEMS_PER_PAGE_USERS } from '../../../constants/pagination.constants';
import { LOGIN_METHODS } from '../../../constants/users.constants';
import { UserExtraApiService, UserFormApiService, UserManagementApiService } from '@/services/user.service';
import { AxiosError } from 'axios';
import { RoleApi } from '@/services/role.service';
import { useImpersonation } from '../AuditManagement/useImpersonation';
import { SortDirection } from '@/components/shared/Generic/GenericTable';
import { DepartmentsApi } from '@/services/departments.service';
import { PrivacyApi } from '@/services/security.service';
import { PositionsApi } from '@/services/positions.service';
import { SelectOption, FilterFieldConfig } from '@/types/management.types';

import { tr } from '@/i18n';
import { roleLabel } from '@/i18n/roleLabels';
const STATUS_OPTIONS: SelectOption[] = [
  { value: "all", label: tr("tags.list.filter_all_statuses", "All Statuses") },
  { value: "deleted", label: tr("user.status_deleted", "Deleted") },
  { value: "suspended", label: tr("common.suspended", "Suspended") },
];

const BOOLEAN_OPTIONS: SelectOption[] = [
  { label: tr("common.yes", "Yes"), value: 'true' },
  { label: tr("common.no", "No"), value: 'false' },
];

const LANGUAGE_OPTIONS: SelectOption[] = [
  { label: tr("lang.code_en", "English"), value: 'en' },
  { label: tr("ui.spanish", "Spanish"), value: 'es' },
  { label: tr("ui.french", "French"), value: 'fr' },
  { label: tr("ui.german", "German"), value: 'de' },
  { label: tr("ui.chinese_3b2611", "Chinese"), value: 'zh' },
];

const staticFilterFields: FilterFieldConfig[] = [
  { name: 'username', placeholder: tr("ui.search", "Search..."), type: 'text' },
  { name: 'role', placeholder: tr("user.col_role", "Role"), type: 'select', options: [] },
  { name: 'department', placeholder: tr("user.col_department", "Department"), type: 'select', options: [] },
  { name: 'position', placeholder: tr("ui.position", "Position"), type: 'select', options: [] },
  { name: 'status', placeholder: tr("common.status", "Status"), type: 'select', options: STATUS_OPTIONS },
  { name: 'loginmethod', placeholder: tr("ui.login_method_2e8714", "Login Method"), type: 'select', options: LOGIN_METHODS },
  { name: 'lang', placeholder: tr("tags.import.lang_label", "Language"), type: 'select', options: LANGUAGE_OPTIONS },
  { name: 'locked', placeholder: tr("dashboards.field_locked", "Locked"), type: 'select', options: BOOLEAN_OPTIONS },
  { name: 'sync', placeholder: tr("ui.sync_d8e87c", "Sync"), type: 'select', options: BOOLEAN_OPTIONS },
];

type ModalAction = "suspend" | "unsuspend" | "delete" | "restore" | "purge";

export const useUsersManagement = () => {
  const { showNotification } = useNotification();
  const { loginAs } = useImpersonation();
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filterFieldsConfig, setFilterFieldsConfig] = useState<FilterFieldConfig[]>(staticFilterFields);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<ModalAction | null>(null);
  const [isBulk, setIsBulk] = useState(false);
  const [targetUserId, setTargetUserId] = useState<number | null>(null);

  const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: SortDirection }>({
    key: null,
    direction: null,
  });

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [rolesRes, departmentsRes, positionsRes] = await Promise.all([
          RoleApi.getRoles(0, 100),
          DepartmentsApi.getDepartments(0, 100),
          PositionsApi.getPositions(0, 100),
        ]);
        const roleOptions: SelectOption[] = rolesRes.items.map(r => ({ label: roleLabel(r.name), value: r.id.toString() }));
        const departmentOptions: SelectOption[] = departmentsRes.items.map(d => ({ label: d.name, value: d.id.toString() }));
        const positionOptions: SelectOption[] = positionsRes.items.map(p => ({ label: p.title, value: p.id.toString() }));

        setFilterFieldsConfig(current => current.map(field => {
          if (field.name === 'role') return { ...field, options: roleOptions };
          if (field.name === 'department') return { ...field, options: departmentOptions };
          if (field.name === 'position') return { ...field, options: positionOptions };
          return field;
        }));
      } catch (err) {
        // This provider wraps every admin page: someone without the user-admin lists (a trainer
        // grading an assignment) gets a 403 here — that's not an error for them.
        if ((err as { response?: { status?: number } })?.response?.status === 403) return;
        showNotification(tr("ui.could_not_load_filter_options", "Could not load filter options."), "error");
      }
    };
    loadFilterOptions();
  }, []);  // run once on mount — showNotification is stable

  const mapUiFiltersToApiParams = useCallback((uiFilters: Record<string, string | undefined>): UserFilterParams => {
    const params: UserFilterParams = {};
    if (uiFilters.username) params.username = uiFilters.username;
    if (uiFilters.role) params.role_id = parseInt(uiFilters.role, 10);
    if (uiFilters.department) params.department_id = parseInt(uiFilters.department, 10);
    if (uiFilters.position) params.position_id = parseInt(uiFilters.position, 10);
    if (uiFilters.status === "deleted") params.deleted = true;
    else if (uiFilters.status === "suspended") params.suspended = true;
    else if (uiFilters.status === "active") {
      params.deleted = false;
      params.suspended = false;
    }
    if (uiFilters.loginmethod) params.loginmethod = uiFilters.loginmethod;
    if (uiFilters.lang) params.lang = uiFilters.lang;
    if (uiFilters.locked) params.locked = uiFilters.locked === 'true';
    if (uiFilters.sync) params.sync = uiFilters.sync === 'true';
    return params;
  }, []);

  const managementHook = useGenericManagement<User, UserFilterParams>({
    apiService: UserManagementApiService,
    entityNameSingular: 'User',
    entityNamePlural: 'Users',
    itemsPerPage: ITEMS_PER_PAGE_USERS,
    initialUiFilterValues: {},
    filterFieldsConfig,
    mapUiFiltersToApiParams,
  });

  const {
    items: users,
    refetchItems,
    handleFilterInputChange: originalHandleFilterChange,
    goToPage,
    uiFilterValues,
    handleSelectFilterChange: originalSelectFilterChange,
    itemsPerPage,
    setItemsPerPage: handleItemsPerPageChange,
    loading,
    error,
    currentPage,
    totalItems,
    handlePreviousPage,
    handleNextPage,
    handleExport,
  } = managementHook;

  const handleSort = (columnKey: string) => {
    let newDirection: SortDirection = 'asc';
    if (sortConfig.key === columnKey) {
      if (sortConfig.direction === 'asc') newDirection = 'desc';
      else if (sortConfig.direction === 'desc') newDirection = null;
    }
    setSortConfig({ key: newDirection ? columnKey : null, direction: newDirection });
  };

  const sortedUsers = [...users].sort((a, b) => {
    if (!sortConfig.key || !sortConfig.direction) return 0;

    let valA: string = '';
    let valB: string = '';

    if (sortConfig.key === 'name') {
      valA = `${a.firstname || ''} ${a.lastname || ''}`.trim() || a.username;
      valB = `${b.firstname || ''} ${b.lastname || ''}`.trim() || b.username;
    } else if (sortConfig.key === 'role') {
      valA = a.roles?.length > 0 ? a.roles[0].name : (a.role_name || '');
      valB = b.roles?.length > 0 ? b.roles[0].name : (b.role_name || '');
    } else if (sortConfig.key === 'department') {
      valA = a.department_name || (a.profile_data?.department as string) || '';
      valB = b.department_name || (b.profile_data?.department as string) || '';
    } else {
      const raw = a[sortConfig.key as keyof User];
      valA = raw !== null && raw !== undefined ? String(raw) : '';
      const rawB = b[sortConfig.key as keyof User];
      valB = rawB !== null && rawB !== undefined ? String(rawB) : '';
    }

    if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
    if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleOpenModal = (action: ModalAction, bulk: boolean, userId: number | null = null) => {
    setModalAction(action);
    setIsBulk(bulk);
    setTargetUserId(userId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalAction(null);
    setTargetUserId(null);
    setIsBulk(false);
  };

  const executeSingleAction = useCallback(async (userId: number, action: ModalAction) => {
    if (action === 'purge') {
      // GDPR: anonymise a deleted account; learning history stays under its id (not restorable).
      try {
        await PrivacyApi.purgeUser(userId);
        showNotification(tr("user.purge_done", "Personal data purged. The account keeps its learning history anonymously and cannot be restored."), 'success');
        refetchItems();
      } catch (err) {
        const error = err as AxiosError<{ detail: string }>;
        showNotification(error.response?.data?.detail || tr("user.purge_failed", "Could not purge this account."), 'error');
      }
      return;
    }
    let updateData: UserUpdatePayload = {};
    switch (action) {
      case "suspend": updateData = { suspended: true }; break;
      case "unsuspend": updateData = { suspended: false }; break;
      case "delete": updateData = { deleted: true }; break;
      case "restore": updateData = { deleted: false, suspended: false }; break;
    }
    try {
      await UserFormApiService.updateItem(userId, updateData);
      showNotification(tr("ui.user_x_action_completed", "User {v0} action completed.", { v0: action }), 'success');
      refetchItems();
    } catch (err) {
      const error = err as AxiosError<{ detail: string }>;
      showNotification(error.response?.data?.detail || tr("ui.failed_to_perform_x_on_user", "Failed to perform {v0} on user.", { v0: action }), 'error');
    }
  }, [refetchItems, showNotification]);

  const executeBulkAction = useCallback(async (action: ModalAction) => {
    const actionPayload: UserBulkActionRequest = { user_ids: selectedUserIds };
    // Unsuspend sent no field at all and the API answered "No valid bulk action" (UAT DEF-US-010).
    if (action === 'suspend') actionPayload.suspend = true;
    else if (action === 'unsuspend') actionPayload.suspend = false;
    else if (action === 'delete') actionPayload.delete = true;
    else if (action === 'restore') actionPayload.restore = true;
    try {
      await UserExtraApiService.performBulkAction(actionPayload);
      showNotification(tr("ui.bulk_action_x_completed_successfully", "Bulk action '{v0}' completed successfully.", { v0: action }), "success");
      setSelectedUserIds([]);
      setSelectAll(false);
      refetchItems();
    } catch (err) {
      const error = err as AxiosError<{ detail: string }>;
      showNotification(error.response?.data?.detail || tr("ui.failed_to_perform_bulk_x", "Failed to perform bulk {v0}.", { v0: action }), "error");
    }
  }, [selectedUserIds, refetchItems, showNotification]);

  const handleConfirmAction = () => {
    if (!modalAction) return;
    if (isBulk) executeBulkAction(modalAction);
    else if (targetUserId) executeSingleAction(targetUserId, modalAction);
    handleCloseModal();
  };

  const handleUserAction = (userId: number, action: ModalAction) => {
    handleOpenModal(action, false, userId);
  };

  const handleBulkAction = (action: ModalAction) => {
    if (selectedUserIds.length > 0) handleOpenModal(action, true);
    else showNotification(tr("role.assign_none_selected", "No users selected."), "warning");
  };

  const handleFilterInputChange = useCallback((name: string, value: string) => {
    originalHandleFilterChange({ target: { name, value } } as React.ChangeEvent<HTMLInputElement>);
  }, [originalHandleFilterChange]);

  const handleClearAllFilters = useCallback(() => {
    Object.keys(uiFilterValues).forEach(key => {
      if (uiFilterValues[key]) {
        const field = filterFieldsConfig.find(f => f.name === key);
        if (field?.type === 'select') originalSelectFilterChange(key, '');
        else handleFilterInputChange(key, '');
      }
    });
  }, [uiFilterValues, filterFieldsConfig, originalSelectFilterChange, handleFilterInputChange]);

  const handleSelectAllChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);
    setSelectedUserIds(isChecked ? users.map(user => user.id) : []);
  }, [users]);

  useEffect(() => {
    if (users.length > 0 && selectedUserIds.length === users.length && users.every(user => selectedUserIds.includes(user.id))) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedUserIds, users]);

  const handleToggleUserSelection = useCallback((userId: number) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  }, []);

  return {
    items: sortedUsers,
    loading,
    error,
    currentPage,
    totalItems,
    itemsPerPage,
    handlePreviousPage,
    handleNextPage,
    handleExport,
    handleItemsPerPageChange,
    uiFilterValues,
    filterFields: filterFieldsConfig,
    handleFilterInputChange,
    handleSelectFilterChange: originalSelectFilterChange,
    handlePageClick: goToPage,
    handleClearAllFilters,
    selectedUserIds,
    selectAll,
    handleSelectAllChange,
    handleToggleUserSelection,
    handleUserAction,
    handleBulkAction,
    isModalOpen,
    modalAction,
    handleCloseModal,
    handleConfirmAction,
    loginAs,
    sortConfig,
    handleSort,
    refetch: refetchItems,
    showNotification,
  };
};